import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Tooltip } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import type { MouseEvent } from 'react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { SilobagTagModal } from '@old/features/silobags/silobag-tag/silobag-tag-modal';
import { useSilobagListContext } from '@siloreal:modules:private/silobag-list/config/context';
import type { SilobagRow } from '@siloreal:modules:private/silobag-list/config/extra/types';
import { archiveSilobagService } from '@siloreal:shared:api/service/silobag/silobag-archive';
import { generateSilobagCertificateService } from '@siloreal:shared:api/service/silobag/silobag-certificate';
import { useRouteController } from '@siloreal:shared:hooks/controller/route/use-route-controller';
import * as UI from '@siloreal:shared:ui';
import { SilobagClosureModal } from '../../../silobag-closure-modal';
import { SilobagExtractionModal } from '../../../silobag-extraction-modal';
import { buildRowActions } from './table-row-actions';
import * as HOOKS from '@siloreal:shared:hooks';

const SILOBAG_LIST_QUERY_KEY = ['silopro-vrr-list'] as const;

type Props = {
  row: SilobagRow;
};

async function downloadCertificate(filePath: string, silobagId: string): Promise<void> {
  const response = await window.fetch(filePath);

  const i18n = HOOKS.useLocale.getState();

  if (!response.ok) {
    throw new Error(`${i18n.t('temp.common.error.certificate_download_failed')} ${response.status}`);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = `certificado-${silobagId}.pdf`;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  link.remove();

  window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
}

export function RowActionsMenu({ row }: Props) {
  const queryClient = useQueryClient();
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isClosureModalOpen, setIsClosureModalOpen] = useState(false);
  const [isExtractionModalOpen, setIsExtractionModalOpen] = useState(false);
  const [isDownloadingCertificate, setIsDownloadingCertificate] = useState(false);
  const [isArchivingSilobag, setIsArchivingSilobag] = useState(false);
  const {
    state: { current },
    async: {
      list: { current: list },
    },
  } = useSilobagListContext();
  const { navigate } = useRouteController<
    Record<string, never>,
    { isGuest?: boolean; pathSilobags?: boolean; siloListId?: string[] }
  >({});

  const stopRowEvent = (event: MouseEvent) => {
    event.stopPropagation();
  };

  const handleTagsAssigned = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: SILOBAG_LIST_QUERY_KEY });
  };

  const i18n = HOOKS.useLocale();

  const handleDownloadCertificate = async (): Promise<void> => {
    const loadingToastId = toast.loading(
      `${i18n.t('temp.common.toasts.loading.generating_certificate_in_silo')} ...`
    );

    setIsDownloadingCertificate(true);

    try {
      const certificate = await generateSilobagCertificateService({ query: row.id });

      await downloadCertificate(certificate.file_path, row.id);
      toast.success(i18n.t('temp.common.toasts.success.certificate_downloaded'), { id: loadingToastId });
    } catch {
      toast.error(i18n.t('temp.common.error.certificate_download'), { id: loadingToastId });
    } finally {
      setIsDownloadingCertificate(false);
    }
  };

  const handleArchiveSilobag = async (): Promise<void> => {
    const loadingToastId = toast.loading(
      `${i18n.t('temp.common.toasts.loading.archiving_silobag')} ${row.id}...`
    );

    setIsArchivingSilobag(true);

    try {
      await archiveSilobagService({ query: row.id });
      await queryClient.invalidateQueries({ queryKey: SILOBAG_LIST_QUERY_KEY });

      toast.success(
        `${i18n.t('temp.common.silobag')} ${row.id} ${i18n.t('temp.common.toasts.success.archived')}`,
        {
          id: loadingToastId,
        }
      );
    } catch {
      toast.error(`${i18n.t('temp.common.toasts.error.archived_silobag')} ${row.id}.`, {
        id: loadingToastId,
      });
    } finally {
      setIsArchivingSilobag(false);
    }
  };

  const actions = buildRowActions({
    row,
    state: current,
    navigate,
    siloListId: list?.data.map((item) => item.id),
    openTagModal: () => setIsTagModalOpen(true),
    downloadCertificate: () => void handleDownloadCertificate(),
    isDownloadingCertificate,
    archiveSilobag: () => void handleArchiveSilobag(),
    isArchivingSilobag,
    closeSilobag: () => setIsClosureModalOpen(true),
    extractSilobag: () => setIsExtractionModalOpen(true),
  });

  if (actions.length === 0) return null;

  return (
    <>
      <Box onClick={stopRowEvent} onMouseDown={stopRowEvent}>
        <UI.dropdown.base
          variant="alternative"
          icon={<MoreVertIcon fontSize="small" />}
          ariaLabel="Más acciones"
          menuAriaLabel="Acciones de la silobolsa"
          tid="silobag-list-row-actions-menu-btn"
          popupTid="silobag-list-row-actions-menu-popup"
          closeOnContentClick
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {actions.map((action) => (
            <Tooltip
              key={action.key}
              title={action.tooltip ?? ''}
              placement="right"
              disableHoverListener={!action.tooltip}
            >
              <Box component="span" sx={{ display: 'block', width: '100%' }}>
                <UI.dropdown.option
                  icon={action.icon}
                  onClick={action.onClick}
                  disabled={action.disabled}
                  tid={`silobag-list-row-action-${action.key}`}
                >
                  {action.label}
                </UI.dropdown.option>
              </Box>
            </Tooltip>
          ))}
        </UI.dropdown.base>
      </Box>

      {isTagModalOpen ? (
        <SilobagTagModal
          open
          setOpen={setIsTagModalOpen}
          siloId={row.id}
          siloName={row.name}
          onTagsAssigned={() => void handleTagsAssigned()}
        />
      ) : null}
      {isClosureModalOpen ? (
        <SilobagClosureModal
          open
          silobagIds={[String(row.id)]}
          onClose={() => setIsClosureModalOpen(false)}
        />
      ) : null}
      {isExtractionModalOpen ? (
        <SilobagExtractionModal
          open
          silobagIds={[String(row.id)]}
          onClose={() => setIsExtractionModalOpen(false)}
        />
      ) : null}
    </>
  );
}
